import { Role } from "../state/api.types";

const ROLE_HIERARCHY = [
  Role.Support,
  Role.Developer,
  Role.OrgAdmin,
] as const satisfies Role[];

export const ROLE_TO_STRING = {
  [Role.OrgAdmin]: "Admin",
  [Role.Developer]: "Developer",
  [Role.Support]: "Support",
} as const satisfies Record<Role, string>;

export const ROLE_OPTIONS = [
  {
    value: Role.Support,
    label: "Support",
    description: "Can review customer account details to help your customers.",
    isDefault: true,
  },
  {
    value: Role.Developer,
    label: "Developer",
    description:
      "Can also manage apps, adjust appearance, and access development tools.",
    isDefault: false,
  },
  {
    value: Role.OrgAdmin,
    label: "Admin",
    description:
      "Has all of the above permissions and can also manage team settings and invites.",
    isDefault: false,
  },
] as const satisfies {
  value: Role;
  label: string;
  description: string;
  isDefault: boolean;
}[];

const getRoleLevel = (role: Role): number => {
  const index = ROLE_HIERARCHY.indexOf(role);
  return index === -1 ? -1 : index;
};

/**
 * Check if a user's role meets or exceeds the required role
 * Respects role hierarchy: admin has developer access, developer has support access
 *
 * @example
 * hasRole("org_admin", "developer") // true - admin includes developer
 * hasRole("developer", "developer") // true - exact match
 * hasRole("developer", "org_admin") // false - developer doesn't have admin
 * hasRole("support", "developer") // false - support doesn't have developer
 */
export const hasRole = (userRole: Role, requiredRole: Role): boolean => {
  const requiredLevel = getRoleLevel(requiredRole);
  const userLevel = getRoleLevel(userRole);

  if (requiredLevel === -1 || userLevel === -1) return false;

  return userLevel >= requiredLevel;
};

export const getPrimaryRole = (roles: Role[]): Role => {
  return (
    roles.find((r) => r === Role.OrgAdmin) ??
    roles.find((r) => r === Role.Developer) ??
    roles.find((r) => r === Role.Support) ??
    Role.Support
  );
};
